/* The WorkRoom sample limits. Storefront guidance, not checkout-server validation. */
(() => {
  'use strict';
  const marker = /\b(?:samples?|swatches|swatch|memos?|cfa)\b/i;
  const material = /fabric|wallpaper|wall[ -]?covering/i;
  const clean = value => String(value || '').trim().toLowerCase();
  const duplicate = 'This sample is already in your cart. You can order one of each sample.';
  const maximum = 'You’ve reached the 18-sample limit. Remove a sample from your cart before adding another.';
  function sample(item) {
    const values = item.options || (item.options_with_values || []).map(option => option.value);
    const variant = item.variant_title || '';
    const title = item.product_title || '';
    return material.test((item.product_type || '') + ' ' + title) &&
      (values.some(value => marker.test(value)) || marker.test(variant) ||
        (marker.test(item.product_type || '') || /\b(?:sample|swatch|memo)\s*$/i.test(title)));
  }
  function identity(item) {
    const options = item.options || (item.options_with_values || []).map(option => option.value);
    return String(item.product_id || item.id) + ':' + options.filter(value => !marker.test(value)).map(clean).join('|');
  }
  function summary(items) {
    let count = 0, repeated = false;
    const groups = new Map();
    for (const item of items) {
      if (!sample(item)) continue;
      const quantity = Math.max(0, Number(item.quantity) || 0);
      count += quantity;
      const key = identity(item);
      const total = (groups.get(key) || 0) + quantity;
      groups.set(key, total);
      if (total > 1) repeated = true;
    }
    return { count, message: repeated ? duplicate : count > 18 ? maximum : '' };
  }
  // Export the same pure rules used by the storefront for automated regression tests.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { sample, identity, summary };
    return;
  }
  if (window.WorkroomSamples) return;
  const root = window.Shopify?.routes?.root || '/';
  const originalFetch = window.fetch.bind(window);
  const registry = new Map();
  let queue = Promise.resolve(), latest = null;
  function scan() {
    document.querySelectorAll('script[data-wr-sample-product]').forEach(element => {
      if (element.dataset.loaded) return;
      const product = JSON.parse(element.textContent);
      for (const variant of product.variants) {
        registry.set(String(variant.id), { ...variant, product_id: product.id,
          product_title: product.title, product_type: product.type });
      }
      element.dataset.loaded = 'true';
    });
  }
  function show(message) {
    const dialog = document.getElementById('wr-sample-dialog');
    dialog.querySelector('[data-wr-sample-message]').textContent = message;
    if (!dialog.open) dialog.showModal();
  }
  function render(cart) {
    latest = cart;
    const result = summary(cart.items);
    document.querySelectorAll('[data-wr-sample-count]').forEach(element => {
      const text = `Samples: ${result.count} of 18`;
      if (element.textContent !== text) element.textContent = text;
    });
    document.querySelectorAll('[data-wr-sample-cart-error]').forEach(element => {
      if (element.textContent !== result.message) element.textContent = result.message;
      element.hidden = !result.message;
    });
    document.querySelectorAll('.cart--item').forEach(row => {
      const item = cart.items[Number(row.dataset.lineNum) - 1];
      if (!item || !sample(item)) return;
      row.querySelectorAll('input[name="quantity"],.quantity-selector--input').forEach(input => input.max = '1');
    });
  }
  async function cart() {
    const response = await originalFetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to check samples.');
    const data = await response.json();
    render(data);
    return data;
  }
  function enqueue(action) {
    const next = queue.then(action, action);
    queue = next.catch(() => {});
    return next;
  }
  function decode(body) {
    if (!body) return {};
    if (typeof body === 'string' && body.trim().startsWith('{')) return JSON.parse(body);
    const entries = body instanceof FormData || body instanceof URLSearchParams ? body : new URLSearchParams(body);
    const data = {}, items = {}, updates = {};
    for (const [key, value] of entries) {
      let match;
      if ((match = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/))) {
        (items[match[1]] ||= {})[match[2]] = value;
      } else if ((match = key.match(/^updates\[([^\]]*)\]$/))) {
        if (match[1]) updates[match[1]] = value;
        else (data.updates ||= []).push(value);
      } else data[key] = value;
    }
    if (Object.keys(items).length) data.items = Object.values(items);
    if (Object.keys(updates).length) data.updates = updates;
    return data;
  }
  function proposed(current, action, body) {
    const items = current.items.map(item => ({ ...item }));
    if (action === 'add') {
      scan();
      for (const added of body.items || [body]) {
        const details = registry.get(String(added.id)) || items.find(item => String(item.id) === String(added.id));
        // Unknown app-created variants must be checked in the cart before checkout.
        if (details) items.push({ ...details, quantity: added.quantity ?? 1 });
      }
    } else if (action === 'change') {
      const item = body.line ? items[Number(body.line) - 1] : items.find(item => item.key === String(body.id) || String(item.id) === String(body.id));
      if (item && body.quantity !== undefined) item.quantity = body.quantity;
    } else if (action === 'update' && body.updates) {
      items.forEach((item, index) => {
        const quantity = Array.isArray(body.updates) ? body.updates[index] : body.updates[item.key] ?? body.updates[item.id];
        if (quantity !== undefined) item.quantity = quantity;
      });
    } else if (action === 'clear') return [];
    return items;
  }
  window.fetch = function(input, init) {
    const url = new URL(typeof input === 'string' || input instanceof URL ? String(input) : input.url, location.href);
    const action = url.origin === location.origin && url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/)?.[1];
    if (!action) return originalFetch(input, init);
    return enqueue(async () => {
      try {
        let body = init?.body;
        if (body === undefined && input instanceof Request) body = await input.clone().text();
        if (body === undefined) body = url.searchParams;
        const current = await cart();
        const projected = proposed(current, action, decode(body));
        const before = summary(current.items), after = summary(projected);
        // Always allow removal/reduction so an already invalid cart can be repaired.
        const reduction = action !== 'add' && projected.every((item, index) => Number(item.quantity) <= Number(current.items[index]?.quantity || 0));
        if (after.message && !reduction) {
          show(after.message);
          return new Response(JSON.stringify({ status: 422, message: after.message, description: after.message }),
            { status: 422, headers: { 'Content-Type': 'application/json' } });
        }
        const response = await originalFetch(input, init);
        if (response.ok) {
          const updated = await cart();
          const result = summary(updated.items);
          if (result.message && (!before.message || action === 'add')) show(result.message);
        }
        return response;
      } catch (error) {
        show('We couldn’t check your sample limits. Please refresh the page and try again.');
        throw error;
      }
    });
  };
  async function checkout() {
    await queue;
    try {
      const result = summary((await cart()).items);
      if (result.message) { show(result.message); return false; }
      return true;
    } catch {
      show('We couldn’t check your sample limits. Please refresh the page and try again.');
      return false;
    }
  }
  function restoreQuantities() {
    if (!latest) return;
    document.querySelectorAll('.cart--item').forEach(row => {
      const item = latest.items[Number(row.dataset.lineNum) - 1];
      if (!item) return;
      row.querySelectorAll('.quantity-selector--input').forEach(input => {
        input.value = item.quantity;
        input.removeAttribute('data-loading');
      });
    });
  }
  window.WorkroomSamples = { checkout, restoreQuantities };
  const replay = new WeakSet();
  // Window capture runs before existing document-level minimum-order checks.
  window.addEventListener('click', async event => {
    const button = event.target.closest?.('[name="checkout"],.cart--checkout-button,a[href]');
    if (!button) return;
    if (button.tagName === 'A' && !/\/checkout\/?$/.test(new URL(button.href, location.href).pathname)) return;
    if (replay.has(button)) { replay.delete(button); return; }
    event.preventDefault(); event.stopImmediatePropagation();
    if (await checkout()) { replay.add(button); button.click(); }
  }, true);
  window.addEventListener('submit', async event => {
    const form = event.target;
    if (replay.has(form)) { replay.delete(form); return; }
    const path = new URL(form.action, location.href).pathname;
    const submitter = event.submitter;
    const checkingOut = submitter?.name === 'checkout' || /\/checkout\/?$/.test(path);
    if (checkingOut) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (await checkout()) { replay.add(form); form.requestSubmit(submitter || undefined); }
      return;
    }
    // Blockshop drawer uses fetch; regular cart-mode forms need the same validation.
    if (/\/cart\/add\/?$/.test(path) && form.querySelector('product-buy-buttons-element')?.dataset.cartType !== 'drawer') {
      scan();
      const variant = registry.get(String(new FormData(form).get('id')));
      if (!variant || !sample(variant)) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const response = await window.fetch(root + 'cart/add.js', { method: 'POST', body: new FormData(form) }).catch(() => null);
      if (response?.ok) location.assign(root + 'cart');
    }
  }, true);
  function init() {
    scan();
    document.getElementById('wr-sample-dialog').querySelector('button').addEventListener('click', () => document.getElementById('wr-sample-dialog').close());
    cart().catch(() => {});
    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; scan(); if (latest) render(latest); });
    }).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('pageshow', event => { if (event.persisted) cart().catch(() => {}); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
